/**
 * Warm Orange Base Theme
 *
 * Shared styling tokens (colors, typography, layout — no project copy, no
 * themeName) used by the youna-orange and lm-bot presets. Project presets
 * spread this base and add their own themeName, consent and disclaimer
 * blocks. Keep project-specific strings OUT of this file: it is bundled
 * into every variant that extends it.
 */
import { ThemeConfig } from '../../components/Widget/ThemeContext';

export const baseWarmOrangeTheme: ThemeConfig = {
    // Title bar configuration
    showTitleBar: false,

    // Colors
    colors: {
        primary: '#FF865D',
        background: '#FEFCF7',
        launcher: '#FF865D',
        userMessage: '#FF865D',
        userMessageText: '#000000',
        assistantMessage: '#ffffff',
        assistantMessageText: '#000000',
        headerBackground: '#FF865D',
        headerText: '#ffffff',
        sendButton: '#ffffff',
        sendButtonIcon: '#000000',
    },

    // Typography
    typography: {
        fontFamily: 'NeogroteskEssAlt-Regular, Montserrat, sans-serif',
        fontSize: {
            base: '16px',
            header: '20px',
            message: '16px',
            small: '14px',
        },
        fontWeight: {
            normal: 400,
            medium: 500,
            bold: 700,
        },
        lineHeight: {
            tight: '1.2',
            normal: '1.5',
            relaxed: '1.75',
        },
    },

    // Spacing
    spacing: {
        message: '12px',
        bubble: '16px',
        container: '20px',
        compact: '8px',
    },

    // Border radius
    borderRadius: {
        bubble: '20px',
        button: '8px',
        window: '20px',
        launcher: '50%',
    },

    // Shadows
    boxShadow: {
        launcher: '0 4px 12px rgba(0, 0, 0, 0.15)',
        window: 'none',
        message: 'none',
    },

    // Dimensions - based on .chat-container
    dimensions: {
        height: 'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 10px)',
        width: '100%',
        maxWidth: '50%',
        minWidth: '500px',
        padding: '10px 0px',

        // Mobile overrides
        mobile: {
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            top: '40px',
            height: 'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 100px)',
            width: '100%',
            maxWidth: '100%',
            minWidth: '100%',
            borderRadius: '0',
            padding: '0px',
        },
    },

    // Layout
    layout: {
        display: 'flex',
        flexDirection: 'column',
        zIndex: 9999,
    },

    // Animations
    animations: {
        enabled: true,
        speed: '0.3s',
        easing: 'ease-in-out',
    },

    // Custom CSS variables
    cssVariables: {
        // User message styling
        '--theme-user-message-border-radius': '20px 20px 0px 20px',
        '--theme-user-message-border': '1px solid #070707',

        // Assistant message styling
        '--theme-assistant-message-border-radius': '20px 20px 20px 0px',
        '--theme-assistant-message-border': '1px solid #000000',

        // Send button styling
        '--theme-send-button-border-radius': '0px 20px 20px 0px',
        '--theme-send-button-border': '1px solid #070707',
        '--theme-send-button-border-left': '0',
        '--theme-send-button-padding': '14px 16px',

        // Input styling
        '--theme-input-border': '1px solid #070707',
        '--theme-input-border-radius': '20px 0px 0px 20px',
        '--theme-input-bg': '#ffffff',
        '--theme-input-padding': '14px 20px',

        // Sender bar styling
        '--theme-sender-border-top': 'none',
        '--theme-sender-padding': '10px 20px 5px 20px',
        '--theme-sender-align-items': 'stretch',
        '--theme-sender-min-height': 'auto',
        '--theme-input-height': 'auto',

        // Background gradient
        '--theme-background-gradient': 'linear-gradient(180deg, #FEFCF7 0%, #E8E4D9 100%)',

        // Suggestion/reply button styling
        '--theme-button-suggestion-bg': '#FF865D',
        '--theme-button-suggestion-border': '1px solid #070707',
        '--theme-button-suggestion-border-radius': '28px 28px 0px 28px',
        '--theme-button-suggestion-hover-bg': '#E4E1A3',

        // Message layout
        '--theme-message-max-width': '85%',
        '--theme-spacing-bubble-horizontal': '24px',

        // Disclaimer styling
        '--theme-disclaimer-color': '#a8acac',
        '--theme-disclaimer-font-size': '9px',

        // Bold font family
        '--theme-font-family-bold': 'NeogroteskEssAlt-BlackIt, Montserrat, sans-serif',
    },
};
